import { useTheme } from "@mui/material/styles";
import {
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarProps,
  ToolbarPropsOverrides,
} from "@mui/x-data-grid-pro";
import { AxiosResponse } from "axios";
import React from "react";
import { useTranslation } from "react-i18next";
import { IJobRegistrationResponse } from "../../../../api/jobsService/IJobsService";
import ControlledHeaderFilter from "../../../../components/DataGridPro/HeaderFilters/controlledHeaderFilter/ControlledHeaderFilter";
import DownloadButton from "../../../../components/Table/components/DownloadButton/DownloadButton";
import { BlobResponse } from "../../../../utils/downloadFile";
import { PendingPromotionCompilationJob } from "../../../../utils/promotions/promotionUtils";

export type InvoicesGridToolBarProps = {
  disablePartNoSearch: boolean;
  partNoPlaceHolder: string;
  partNoValue: string;
  partNoOnChange: (value: string) => void;
  partNoOnClear: () => void;
  showDownloadButton: boolean;
  showConsolidateInvoiceButton: boolean;
  downloadInvoicesCallback: (
    accessToken: string,
    lang?: string,
    objTypeCode?: string,
    fileName?: string
  ) => Promise<
    AxiosResponse<
      BlobResponse | PendingPromotionCompilationJob | IJobRegistrationResponse
    >
  >;
  consolidateInvoicesCallback: (
    accessToken: string,
    lang?: string,
    objTypeCode?: string,
    fileName?: string
  ) => Promise<
    AxiosResponse<
      BlobResponse | PendingPromotionCompilationJob | IJobRegistrationResponse
    >
  >;
  setUnauthorizedError: (value: boolean) => void;
  disableConsolidatedInvoiceDownloadButton: boolean;
  isConsolidateDownloadInProgress: boolean;
  isDownloadingAllInProgress: boolean;
  showConsolidateInvoicesToExcelButton?: boolean;
  disableConsolidateInvoicesToExcelButton?: boolean;
  consolidateInvoicesToExcelCallback?: (
    accessToken: string
  ) => Promise<AxiosResponse<BlobResponse>>;
  onConsolidateInvoicesToExcelSuccess?: () => void;
};

type MuiToolbarProps = GridToolbarProps & ToolbarPropsOverrides;

const InvoicesGridToolBar = ({
  disablePartNoSearch,
  partNoPlaceHolder,
  partNoValue,
  partNoOnChange,
  partNoOnClear,
  showDownloadButton,
  showConsolidateInvoiceButton,
  downloadInvoicesCallback,
  consolidateInvoicesCallback,
  setUnauthorizedError,
  disableConsolidatedInvoiceDownloadButton,
  isConsolidateDownloadInProgress,
  isDownloadingAllInProgress,
  showConsolidateInvoicesToExcelButton = false,
  disableConsolidateInvoicesToExcelButton = true,
  consolidateInvoicesToExcelCallback,
  onConsolidateInvoicesToExcelSuccess,
}: MuiToolbarProps & InvoicesGridToolBarProps) => {
  const { t } = useTranslation("invoices");
  const theme = useTheme();

  const consolidateInvoiceText = t(
    "invoices:consolidateInvoices.download.text.v2"
  );
  const downloadToExcelText = t(
    "invoices:consolidateInvoices.label.downloadToExcel"
  );
  const consolidateDownloadToExcelText = t(
    "invoices:consolidateInvoices.label.consolidateDownloadToExcel"
  );
  const buttonInProgressText = t(
    "invoices:consolidateInvoices.download.text.v2"
  );
  const buttonTooltip = t("invoices:consolidateInvoices.button.tooltip.v2");
  const buttonInProgressTooltip = t(
    "invoices:consolidateInvoices.button.inProgress.tooltip.v2"
  );
  return (
    <GridToolbarContainer
      sx={{
        display: "flex",
        gap: theme.tokens.FuelSpacing6,
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <div style={{ display: "flex", gap: theme.tokens.FuelSpacing6 }}>
        <GridToolbarColumnsButton
          data-testid="invoice-list-toolbar-column-button"
          slotProps={{
            button: {
              variant: "text",
              size: "medium",
            },
          }}
        />
        <ControlledHeaderFilter
          inputRef={null as unknown as React.RefObject<unknown>}
          dataTestId={partNoPlaceHolder}
          disabled={disablePartNoSearch}
          placeHolder={partNoPlaceHolder}
          value={partNoValue}
          onChange={(value) => partNoOnChange(value)}
          onClear={partNoOnClear}
          sx={{ width: "220px" }}
        />
      </div>
      <div style={{ display: "flex", gap: theme.tokens.FuelSpacing2 }}>
        {showConsolidateInvoiceButton && (
          <DownloadButton
            downloadButtonProps={{
              downloadFunction: consolidateInvoicesCallback,
              downloadInProgress: isConsolidateDownloadInProgress,
              shouldDisableDownloadButton:
                disableConsolidatedInvoiceDownloadButton,
              setShowUnauthorizedError: setUnauthorizedError,
              isRealTimeDownload: false,
              variant: "text",
              buttonText: consolidateInvoiceText,
              buttonInProgressText,
              buttonTooltip,
              buttonInProgressTooltip,
              isAttachmentButton: true,
            }}
          />
        )}
        {showConsolidateInvoicesToExcelButton &&
          consolidateInvoicesToExcelCallback && (
            <DownloadButton
              downloadButtonProps={{
                downloadFunction: consolidateInvoicesToExcelCallback,
                shouldDisableDownloadButton:
                  disableConsolidateInvoicesToExcelButton,
                setShowUnauthorizedError: setUnauthorizedError,
                isRealTimeDownload: true,
                variant: "text",
                buttonText: consolidateDownloadToExcelText,
                buttonInProgressText: consolidateDownloadToExcelText,
                isAttachmentButton: true,
                onSuccess: onConsolidateInvoicesToExcelSuccess,
              }}
            />
          )}
        {showDownloadButton && (
          <DownloadButton
            downloadButtonProps={{
              downloadFunction: downloadInvoicesCallback,
              isRealTimeDownload: false,
              shouldDisableDownloadButton: false,
              setShowUnauthorizedError: setUnauthorizedError,
              variant: "text",
              buttonText: downloadToExcelText,
              buttonInProgressTooltip:
                t("invoices:consolidateInvoices.button.inProgress.tooltip") ||
                "The file is getting downloaded",
              downloadInProgress: isDownloadingAllInProgress,
              isAttachmentButton: true,
            }}
          />
        )}
      </div>
    </GridToolbarContainer>
  );
};

export default InvoicesGridToolBar;
